import {
  Create, Datagrid, DateField, Edit, FunctionField, List, SaveButton, SimpleForm, TextField,
  TextInput, Toolbar, TopToolbar, useGetIdentity, useNotify, useRecordContext, useRedirect,
  useRefresh, required, email as emailRule
} from 'react-admin';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, Stack, Tooltip, Typography
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { callAction } from './dataProvider';
import { BackLink } from './back';

/**
 * Who may sign in to the admin.
 *
 * Only the owner ever sees this screen - the menu hides it and every endpoint
 * behind it answers "no such route" to anyone else. What is left to design for
 * is the owner's own mistakes, so the destructive actions are the ones that
 * carry the confirmation and the account you are signed in as is never one of
 * the things you can switch off.
 *
 * Passwords are generated here rather than chosen. Nobody types a password
 * into this form, so there is nothing to leak into a browser history or a
 * screen share beyond the one dialog that shows it, and the stored form is a
 * scrypt hash either way.
 */

interface AdminRecord {
  id: number;
  email: string;
  name: string;
  isActive: boolean;
  isOwner: boolean;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  failedAttempts: number;
  created: string;
  /** Present only on the record returned by a create. */
  generatedPassword?: string;
}

/* ------------------------------------------------------------------ list */

function StatusChip() {
  const record = useRecordContext<AdminRecord>();
  if (record == null) return null;

  if (record.isOwner) {
    return (
      <Tooltip title="Vlastník administrácie. Tento účet spravuje ostatné a nemožno ho deaktivovať ani odstrániť.">
        <Chip size="small" color="primary" label="Vlastník" />
      </Tooltip>
    );
  }
  if (!record.isActive) return <Chip size="small" color="default" variant="outlined" label="Deaktivovaný" />;
  if (record.lockedUntil != null) {
    return (
      <Tooltip title="Zamknutý po opakovaných neúspešných prihláseniach. Odomknúť možno v detaile účtu.">
        <Chip size="small" color="warning" label="Zamknutý" />
      </Tooltip>
    );
  }
  return <Chip size="small" color="success" variant="outlined" label="Aktívny" />;
}

export function AdminList() {
  return (
    <List
      title="Prístupy do administrácie"
      perPage={50}
      sort={{ field: 'id', order: 'ASC' }}
      exporter={false}
      pagination={false}
      actions={<AdminListActions />}
      empty={false}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="name" label="Meno" />
        <TextField source="email" label="E-mail" />
        <FunctionField label="Stav" render={() => <StatusChip />} />
        <DateField source="lastLoginAt" label="Posledné prihlásenie" showTime emptyText="Zatiaľ nikdy" />
        <DateField source="created" label="Vytvorený" />
      </Datagrid>
    </List>
  );
}

function AdminListActions() {
  const redirect = useRedirect();

  return (
    <TopToolbar>
      <Button variant="contained" size="small" onClick={() => redirect('create', 'admins')}>
        Pridať prístup
      </Button>
    </TopToolbar>
  );
}

/* -------------------------------------------------------- password dialog */

/**
 * The password, shown once.
 *
 * It cannot be recovered afterwards - only the hash is kept - so this dialog
 * says so plainly rather than letting somebody close it expecting to find the
 * password again in the list.
 */
function PasswordDialog({ email, password, onClose }: {
  email: string;
  password: string;
  onClose: () => void;
}) {
  const notify = useNotify();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      notify('Heslo skopírované do schránky.', { type: 'info' });
    } catch {
      // Clipboard access is refused in some browsers; the password is on
      // screen and selectable, so this is a convenience, not the only way.
      notify('Heslo sa nepodarilo skopírovať — označte ho a skopírujte ručne.', { type: 'warning' });
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Heslo pre {email}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div" sx={{ mb: 2 }}>
          Odovzdajte ho používateľovi bezpečným kanálom. <b>Zobrazuje sa iba teraz</b> — uložené je
          len v nečitateľnej podobe, takže ho neskôr nemožno zobraziť, iba vygenerovať nové.
        </DialogContentText>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            component="code"
            sx={{
              flex: 1, p: 1.5, borderRadius: 1, bgcolor: 'action.hover',
              fontFamily: 'monospace', fontSize: '1.05rem', letterSpacing: '.02em',
              userSelect: 'all', wordBreak: 'break-all'
            }}
          >
            {password}
          </Box>
          <Tooltip title="Kopírovať">
            <IconButton onClick={copy} color={copied ? 'success' : 'default'}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Mám ho uložené</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- create */

export function AdminCreate() {
  const notify = useNotify();
  const redirect = useRedirect();
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  return (
    <>
      <Create
        title="Nový prístup"
        // The password comes back in this response and nowhere else, so the
        // redirect waits until the dialog has been acknowledged.
        mutationOptions={{
          onSuccess: (data: AdminRecord) => {
            notify('Prístup vytvorený.', { type: 'info' });
            if (data.generatedPassword != null) {
              setIssued({ email: data.email, password: data.generatedPassword });
            } else {
              redirect('list', 'admins');
            }
          }
        }}
        actions={<TopToolbar sx={{ justifyContent: 'flex-start' }}><BackLink /></TopToolbar>}
      >
        <SimpleForm>
          <Alert severity="info" variant="outlined" sx={{ mb: 2, width: '100%' }}>
            Heslo sa vygeneruje automaticky a zobrazí sa raz, hneď po vytvorení účtu. Nový účet má
            rovnaké práva ako ostatní administrátori — spravovať prístupy môže naďalej len vlastník.
          </Alert>
          <TextInput
            source="email"
            label="E-mail"
            validate={[required(), emailRule()]}
            helperText="Adresa, ktorou sa bude prihlasovať."
            fullWidth
          />
          <TextInput
            source="name"
            label="Meno"
            validate={required()}
            helperText="Podľa neho sa účet pozná v zozname a v histórii zmien."
            fullWidth
          />
        </SimpleForm>
      </Create>

      {issued != null && (
        <PasswordDialog
          email={issued.email}
          password={issued.password}
          onClose={() => {
            setIssued(null);
            redirect('list', 'admins');
          }}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ edit */

/** Reset, unlock and remove — the things that are not field edits. */
function AdminActions() {
  const record = useRecordContext<AdminRecord>();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  if (record == null) return null;

  const isSelf = identity?.id === record.id;

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } catch (error) {
      notify((error as Error).message || 'Akciu sa nepodarilo dokončiť.', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const reset = () =>
    run(async () => {
      const result = (await callAction(`admins/${record.id}/password`)) as { password: string };
      setIssued({ email: record.email, password: result.password });
      refresh();
    });

  const unlock = () =>
    run(async () => {
      await callAction(`admins/${record.id}/unlock`);
      notify('Účet odomknutý.', { type: 'info' });
      refresh();
    });

  const remove = () =>
    run(async () => {
      await callAction(`admins/${record.id}`, 'DELETE');
      notify('Prístup odstránený.', { type: 'info' });
      setConfirmDelete(false);
      redirect('list', 'admins');
    });

  return (
    <Stack spacing={2} sx={{ width: '100%', mt: 3 }}>
      <Typography variant="overline" color="text.secondary">Akcie</Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" startIcon={<KeyIcon />} onClick={reset} disabled={busy}>
          Vygenerovať nové heslo
        </Button>

        {record.lockedUntil != null && (
          <Button size="small" variant="outlined" color="warning" startIcon={<LockOpenIcon />} onClick={unlock} disabled={busy}>
            Odomknúť účet
          </Button>
        )}

        {/* Neither the owner nor the account you are signed in as. The API
            refuses both regardless; hiding them keeps the owner from
            discovering that by being told no. */}
        {!record.isOwner && !isSelf && (
          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)} disabled={busy}>
            Odstrániť prístup
          </Button>
        )}
      </Stack>

      {record.failedAttempts > 0 && record.lockedUntil == null && (
        <Alert severity="warning" variant="outlined">
          Posledné prihlásenia zlyhali {record.failedAttempts}× po sebe. Po piatich sa účet na 15 minút zamkne.
        </Alert>
      )}

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Odstrániť prístup pre {record.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Účet <b>{record.email}</b> sa už nebude môcť prihlásiť. História jeho zmien zostáva
            zachovaná. Ak ide len o dočasné prerušenie, namiesto odstránenia účet deaktivujte.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Zrušiť</Button>
          <Button onClick={remove} color="error" variant="contained" disabled={busy}>Odstrániť</Button>
        </DialogActions>
      </Dialog>

      {issued != null && (
        <PasswordDialog email={issued.email} password={issued.password} onClose={() => setIssued(null)} />
      )}
    </Stack>
  );
}

function AdminFormBody() {
  const record = useRecordContext<AdminRecord>();
  const { identity } = useGetIdentity();
  if (record == null) return null;

  const isSelf = identity?.id === record.id;

  return (
    <>
      {record.isOwner && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2, width: '100%' }}>
          Toto je vlastník administrácie — jediný účet, ktorý spravuje prístupy. Nemožno ho
          deaktivovať ani odstrániť, aby sa administrácia nedala uzamknúť zvonku.
        </Alert>
      )}

      <TextInput source="name" label="Meno" validate={required()} fullWidth />
      <TextInput
        source="email"
        label="E-mail"
        validate={[required(), emailRule()]}
        helperText="Zmena adresy zmení aj prihlasovacie meno."
        fullWidth
      />

      <ActiveToggle locked={record.isOwner || isSelf} owner={record.isOwner} self={isSelf} />
      <AdminActions />
    </>
  );
}

/**
 * A switch rather than a checkbox, and disabled where the API would refuse.
 * Written by hand instead of BooleanInput so the reason can sit next to it.
 */
function ActiveToggle({ locked, owner, self }: { locked: boolean; owner: boolean; self: boolean }) {
  const record = useRecordContext<AdminRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  if (record == null) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      await callAction(`admins/${record.id}`, 'PATCH', { isActive: !record.isActive });
      notify(record.isActive ? 'Účet deaktivovaný.' : 'Účet aktivovaný.', { type: 'info' });
      refresh();
    } catch (error) {
      notify((error as Error).message || 'Zmenu sa nepodarilo uložiť.', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const reason = owner
    ? 'Vlastníka nemožno deaktivovať.'
    : self
      ? 'Nemôžete deaktivovať účet, pod ktorým ste prihlásení.'
      : record.isActive
        ? 'Deaktivovaný účet sa nebude môcť prihlásiť. Zmena platí okamžite, aj pre otvorené prihlásenie.'
        : 'Aktivácia zároveň zruší prípadné zamknutie po neúspešných prihláseniach.';

  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Chip
          size="small"
          color={record.isActive ? 'success' : 'default'}
          variant={record.isActive ? 'outlined' : 'filled'}
          label={record.isActive ? 'Aktívny' : 'Deaktivovaný'}
        />
        {!locked && (
          <Button size="small" onClick={toggle} disabled={busy}>
            {record.isActive ? 'Deaktivovať' : 'Aktivovať'}
          </Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">{reason}</Typography>
    </Stack>
  );
}

export function AdminEdit() {
  return (
    <Edit
      title="Prístup"
      mutationMode="pessimistic"
      redirect={false}
      actions={<TopToolbar sx={{ justifyContent: 'flex-start' }}><BackLink /></TopToolbar>}
    >
      {/* No delete in the toolbar: removal lives with the other irreversible
          actions below, behind a confirmation that names the account. */}
      <SimpleForm toolbar={<Toolbar><SaveButton /></Toolbar>}>
        <AdminFormBody />
      </SimpleForm>
    </Edit>
  );
}
